<div class="ctm">
  <div class="ctm__panes">

    <!-- Left: system template types and the organisation's own templates, one view each -->
    <section class="ctm__pane ctm__pane--types">
      <!--
        A vertical switch, not the shared <sb-tabs>, for two reasons. This pane is 18rem wide, and
        `.mode-tabs > div` carries a global `min-width: 161px` with !important font, padding and border
        (styles/_faria-alignment.scss), so two tabs need ~350px here and get clipped behind a
        scrollbar. And an sb-tabs tab is a bare `<div (click)>` with no role, tabindex or key handling,
        i.e. mouse-only. Giving each label the full pane width solves both, and leaves room for a
        translation half again as long as the English.

        Deliberately OUTSIDE the `!loadingCatalogue && !error` gate below. It used to sit inside it,
        which was harmless while every tab needed the catalogue — but the footer has its own endpoint
        and its own storage row, so a failure in any one of the four catalogue calls (they are a
        forkJoin: templates, types, user templates, merge tags) would otherwise make the footer
        un-editable, and every organisation switch in the SysAdmin host would make the whole strip
        vanish and reappear.
      -->
      <div class="ctm__switch" role="tablist" aria-orientation="vertical">
        <button
          *ngFor="let t of listTabs; let i = index"
          type="button"
          role="tab"
          class="ctm__switch-tab"
          [class.ctm__switch-tab--active]="activeListTab === t.id"
          [id]="'ctmListTab-' + t.id"
          [attr.aria-selected]="activeListTab === t.id"
          [attr.aria-controls]="'ctmListPanel-' + t.id"
          [attr.tabindex]="activeListTab === t.id ? 0 : -1"
          [ngbTooltip]="t.hint ? (t.hint | translate) : ''"
          tooltipClass="ctm-tooltip"
          placement="right"
          container="body"
          [openDelay]="300"
          (click)="selectListTab(t.id)"
          (keydown)="onSwitchKeydown($event, i)">
          <span class="ctm__switch-label">{{ t.label | translate }}</span>
          <span class="ctm__switch-count" *ngIf="countFor(t) !== null">{{ countFor(t) }}</span>
        </button>
      </div>

      <div *ngIf="loadingCatalogue" class="ctm__hint">{{ 'SB_Loading' | translate }}</div>
      <div *ngIf="error" class="alert alert-danger">{{ 'SB_Error_loading' | translate }}</div>

      <ng-container *ngIf="!loadingCatalogue && !error">
        <!--
          Both panels stay in the DOM so every `aria-controls` resolves; `[hidden]` decides what is on
          screen and the inner *ngIf keeps the hidden panel's rows out of the DOM.
        -->
        <div
          class="ctm__panel"
          id="ctmListPanel-system"
          role="tabpanel"
          aria-labelledby="ctmListTab-system"
          [hidden]="activeListTab !== 'system'">
          <ng-container *ngIf="activeListTab === 'system'">
            <input
              class="form-control form-control-sm ctm__search"
              type="text"
              [(ngModel)]="typeSearch"
              (ngModelChange)="onTypeSearchChange()"
              [placeholder]="'SB_Search' | translate" />

            <ul class="ctm__list">
              <li
                class="ctm__item"
                *ngFor="let row of catalogue; trackBy: trackCatalogueRow"
                [class.ctm__item--active]="selectionKind === 'type' && selectedTypeId === row.type.id"
                (click)="selectType(row.type.id)">
                <span>{{ row.type.name }}</span>
                <!--
                  What a school wants to know at a glance is which of its emails it has made its own, not
                  which editor a template was last saved in - so a check in the brand colour for a
                  customised template, where an "Unlayer / Legacy" tag used to be.
                -->
                <span
                  class="ctm__customised"
                  *ngIf="row.template?.isCustomised"
                  role="img"
                  [attr.aria-label]="'SB_Template_Customised' | translate"
                  [ngbTooltip]="'SB_Template_Customised' | translate"
                  tooltipClass="ctm-tooltip"
                  placement="right"
                  container="body"
                  [openDelay]="300">
                  <sb-icon name="tick"></sb-icon>
                </span>
              </li>
            </ul>
          </ng-container>
        </div>

        <!-- Custom templates: the organisation's own user-created templates (SB-14890). -->
        <div
          class="ctm__panel ctm__mytemplates"
          id="ctmListPanel-custom"
          role="tabpanel"
          aria-labelledby="ctmListTab-custom"
          [hidden]="activeListTab !== 'custom'">
          <ng-container *ngIf="activeListTab === 'custom'">
            <!--
              Stacked: an 18rem pane cannot hold a school name and the button side by side without
              squeezing the name into a column of single words.
            -->
            <div class="ctm__mytemplates-head">
              <!-- The switch row is named for everyone; the caption says whose templates these are. -->
              <span class="ctm__mytemplates-org" *ngIf="organisationName">{{ organisationName }}</span>
              <button type="button" class="btn btn-primary ctm__mytemplates-new" (click)="newUserTemplate()">
                <sb-icon name="plus"></sb-icon> {{ 'SB_New_Template' | translate }}
              </button>
            </div>

            <div *ngIf="!userTemplates.length" class="ctm__hint">{{ 'SB_No_User_Templates' | translate }}</div>

            <ul class="ctm__list" *ngIf="userTemplates.length">
              <li
                class="ctm__item ctm__item--user"
                *ngFor="let ut of userTemplates"
                [class.ctm__item--active]="selectionKind === 'user' && selectedUserTemplateId === ut.id"
                (click)="selectUserTemplate(ut.id)">
                <span class="ctm__item-name">{{ ut.name }}</span>
                <span class="ctm__tags" *ngIf="ut.tags?.length">
                  <span class="ctm__tag" *ngFor="let tag of ut.tags">{{ tag }}</span>
                </span>
              </li>
            </ul>
          </ng-container>
        </div>
      </ng-container>
    </section>

    <!--
      Right: editor. [hidden], never *ngIf — this wraps sb-template-editor, whose Unlayer
      EmailEditorComponent has no ngOnDestroy, so an *ngIf would leak an iframe on every tab switch.
      `*[hidden] { display: none !important }` (styles/_layout.scss) beats the pane's display:flex.
    -->
    <section class="ctm__pane ctm__pane--editor" [hidden]="activeListTab === 'footer' || activeListTab === 'replyTo'">
      <div class="ctm__edit">
        <div *ngIf="loadingTemplate" class="ctm__hint">{{ 'SB_Loading' | translate }}</div>
        <div *ngIf="!loadingTemplate && !template" class="ctm__hint">{{ 'SB_Select_Template' | translate }}</div>

        <!-- The selected template's name and settings, as one card. -->
        <section class="ctm__card" *ngIf="!loadingTemplate && template">
          <div class="ctm__head">
            <!-- A custom template being created has no saved name to head the card with yet. -->
            <h4 class="ctm__title">{{ selectionTitle || ('SB_New_Template' | translate) }}</h4>
            <span class="ctm__eyebrow">{{ (isUserTemplate ? 'SB_Custom_Template' : 'SB_System_Template') | translate }}</span>
          </div>

          <!--
            Says up front why the settings below cannot be changed, rather than leaving them just grey: an
            orange info bar, keeping the lock. Its text sits in a <div> because the global .alert puts its
            own warning icon before a direct <span> or <p>.
          -->
          <div
            class="alert alert-warning ctm__alert ctm__lock-note"
            role="note"
            *ngIf="!isEditingActiveVersion && !viewingDefault">
            <sb-icon name="locked"></sb-icon>
            <div>{{ 'SB_Template_Settings_Locked' | translate }}</div>
          </div>

          <div class="ctm__meta" *ngIf="!viewingDefault && template as t">
            <div class="ctm__fields">
              <div class="form-group" *ngIf="isUserTemplate">
                <label class="control-label">{{ 'SB_Template_Name' | translate }}</label>
                <input class="form-control" type="text" maxlength="200" [(ngModel)]="t.name" [ngModelOptions]="{ standalone: true }" />
                <small class="text-danger" *ngIf="showRequiredHints && !t.name?.trim()">
                  {{ 'SB_Template_Name_Required' | translate }}
                </small>
              </div>

              <div class="form-group">
                <label class="control-label" for="ctmEmailSubject">{{ 'SB_Email_Subject' | translate }}</label>
                <!--
                  A custom template's subject is copied into a 250-character column on a send, so it is capped
                  here and counted (SB-17213); a type template's subject is not, and has neither.
                -->
                <input
                  id="ctmEmailSubject"
                  class="form-control"
                  type="text"
                  [attr.maxlength]="subjectMaxLength"
                  [attr.aria-describedby]="subjectMaxLength != null ? 'ctmEmailSubjectCount' : null"
                  [(ngModel)]="t.emailSubject"
                  [ngModelOptions]="{ standalone: true }" />
                <small
                  id="ctmEmailSubjectCount"
                  class="d-block"
                  *ngIf="subjectMaxLength != null"
                  [class.text-danger]="subjectTooLong"
                  [class.text-muted]="!subjectTooLong">
                  {{
                    'SB_Template_Subject_Character_Count'
                      | translate: { count: t.emailSubject?.length ?? 0, max: subjectMaxLength }
                  }}
                </small>
                <small class="text-danger" *ngIf="showRequiredHints && !t.emailSubject?.trim()">
                  {{ 'SB_Email_Subject_Required' | translate }}
                </small>
              </div>

              <!--
                Settings that belong to the template rather than to one version. Shares `ctm__flags` with
                the recipients: both are checkbox groups, and both lock while an older version is open.
              -->
              <div class="ctm__flags" role="group" aria-labelledby="ctmTemplateSettingsLabel">
                <span class="ctm__eyebrow" id="ctmTemplateSettingsLabel">{{ 'SB_Template_Settings' | translate }}</span>

                <!--
                  Per-template Reply-To override (SB-15933). Collapsed by default: most templates inherit the
                  organisation's Reply-To, and two always-visible fields made that look like a decision every
                  author had to take. The whole block honours [disabled]="!isEditingActiveVersion" because
                  Reply-To lives on the template header, not on the version being viewed.

                  Hidden for custom (user) templates, where it would do nothing: the composer copies a user
                  template's subject and body into the message, and the resulting communication carries a
                  template TYPE id with no reference back — so the send path resolves Reply-To from the type
                  row and never reads the user template's own. Offering the control there would let an
                  administrator configure a Reply-To that can never reach an email. The column and the API
                  keep the capability for when the send path can carry a user-template reference.
                -->
                <div class="ctm__reply-to-override" *ngIf="!isUserTemplate">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="ctmOverrideReplyTo"
                      [checked]="t.replyToMode !== replyToModes.Inherit"
                      [disabled]="!isEditingActiveVersion"
                      (change)="onOverrideReplyToChange(t, $event)" />
                    <label class="form-check-label" for="ctmOverrideReplyTo">{{ 'SB_Override_Reply_To' | translate }}</label>
                  </div>

                  <!-- Off: say what the template actually sends with, rather than leaving it blank. -->
                  <p class="ctm__muted ctm__reply-to-inherited" *ngIf="t.replyToMode === replyToModes.Inherit">
                    {{ inheritedReplyToLabel | translate: { value: inheritedReplyToText } }}
                  </p>

                  <ng-container *ngIf="t.replyToMode !== replyToModes.Inherit">
                    <div class="ctm__reply-to-modes">
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="radio"
                          id="ctmTemplateReplyToCustom"
                          name="ctmTemplateReplyToMode"
                          [checked]="t.replyToMode === replyToModes.Custom"
                          [disabled]="!isEditingActiveVersion"
                          (change)="onTemplateReplyToModeChange(t, replyToModes.Custom)" />
                        <label class="form-check-label" for="ctmTemplateReplyToCustom">{{ 'SB_Reply_To_Mode_Custom' | translate }}</label>
                      </div>

                      <div class="ctm__reply-to" *ngIf="t.replyToMode === replyToModes.Custom">
                        <div class="form-group">
                          <label class="control-label">{{ 'SB_Reply_To_Email' | translate }}</label>
                          <input
                            class="form-control"
                            type="email"
                            maxlength="320"
                            autocomplete="off"
                            [(ngModel)]="t.replyToEmailAddress"
                            [ngModelOptions]="{ standalone: true }"
                            [disabled]="!isEditingActiveVersion" />
                          <small class="text-danger" *ngIf="isEditingActiveVersion && templateReplyToAddressMissing">
                            {{ 'SB_Reply_To_Required' | translate }}
                          </small>
                          <small class="text-danger" *ngIf="isEditingActiveVersion && !templateReplyToAddressMissing && !replyToEmailValid">
                            {{ 'SB_Invalid_Reply_To_Email' | translate }}
                          </small>
                        </div>

                        <div class="form-group">
                          <label class="control-label">{{ 'SB_Reply_To_Name' | translate }}</label>
                          <input
                            class="form-control"
                            type="text"
                            maxlength="200"
                            [(ngModel)]="t.replyToDisplayName"
                            [ngModelOptions]="{ standalone: true }"
                            [disabled]="!isEditingActiveVersion" />
                        </div>
                      </div>

                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="radio"
                          id="ctmTemplateReplyToNoReply"
                          name="ctmTemplateReplyToMode"
                          [checked]="t.replyToMode === replyToModes.NoReply"
                          [disabled]="!isEditingActiveVersion"
                          (change)="onTemplateReplyToModeChange(t, replyToModes.NoReply)" />
                        <label class="form-check-label" for="ctmTemplateReplyToNoReply">{{ 'SB_Reply_To_Mode_No_Reply' | translate }}</label>
                      </div>
                    </div>

                    <p class="ctm__muted ctm__reply-to-guidance">{{ templateReplyToGuidanceLabel | translate }}</p>
                  </ng-container>
                </div>

                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="ctmIncludeFooter"
                    [(ngModel)]="t.isOrganisationFooterIncluded"
                    [ngModelOptions]="{ standalone: true }"
                    [disabled]="!isEditingActiveVersion" />
                  <label class="form-check-label" for="ctmIncludeFooter">{{ 'SB_Include_Footer_Toggle' | translate }}</label>
                </div>

                <!--
                  User templates only. The flag is read in exactly two places, both on the compose picker
                  (ICommunicationTemplateManager.GetEnabledUserTemplatesAsync and
                  GetComposableUserTemplateAsync) and both user-template-specific. Nothing in either send
                  engine reads it — so on a system template it looked like an off switch for the automatic
                  emails of that type while changing nothing at all.
                -->
                <div class="form-check" *ngIf="isUserTemplate">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="ctmTemplateEnabled"
                    [(ngModel)]="t.isEnabled"
                    [ngModelOptions]="{ standalone: true }"
                    [disabled]="!isEditingActiveVersion" />
                  <label class="form-check-label" for="ctmTemplateEnabled">{{ 'SB_Template_Enabled' | translate }}</label>
                </div>
              </div>
            </div>

            <div class="ctm__flags ctm__flags--recipients" role="group" aria-labelledby="ctmRecipientsLabel">
              <span class="ctm__eyebrow" id="ctmRecipientsLabel">{{ 'SB_Template_Recipients' | translate }}</span>

              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="ctmSendToStudents"
                  [(ngModel)]="t.sendToStudents"
                  [ngModelOptions]="{ standalone: true }"
                  [disabled]="!isEditingActiveVersion" />
                <label class="form-check-label" for="ctmSendToStudents">{{ 'SB_Students' | translate }}</label>
              </div>

              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="ctmSendToParents"
                  [(ngModel)]="t.sendToParents"
                  [ngModelOptions]="{ standalone: true }"
                  [disabled]="!isEditingActiveVersion" />
                <label class="form-check-label" for="ctmSendToParents">{{ 'SB_Parents' | translate }}</label>
              </div>

              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="ctmSendToStaff"
                  [(ngModel)]="t.sendToStaff"
                  [ngModelOptions]="{ standalone: true }"
                  [disabled]="!isEditingActiveVersion" />
                <label class="form-check-label" for="ctmSendToStaff">{{ 'SB_Staff' | translate }}</label>
              </div>
            </div>
          </div>

          <!-- Read-only summary of the platform default's settings (no inputs — nothing here is editable). -->
          <dl class="ctm__default-meta" *ngIf="viewingDefault && defaultTemplate as d">
            <div>
              <dt>{{ 'SB_Email_Subject' | translate }}</dt>
              <dd>{{ d.emailSubject || '—' }}</dd>
            </div>
            <div>
              <dt>{{ 'SB_Send_To_Students' | translate }}</dt>
              <dd>{{ (d.sendToStudents ? 'SB_Yes' : 'SB_No') | translate }}</dd>
            </div>
            <div>
              <dt>{{ 'SB_Send_To_Parents' | translate }}</dt>
              <dd>{{ (d.sendToParents ? 'SB_Yes' : 'SB_No') | translate }}</dd>
            </div>
            <div>
              <dt>{{ 'SB_Send_To_Staff' | translate }}</dt>
              <dd>{{ (d.sendToStaff ? 'SB_Yes' : 'SB_No') | translate }}</dd>
            </div>
            <div>
              <dt>{{ 'SB_Include_Footer_Toggle' | translate }}</dt>
              <dd>{{ (d.isOrganisationFooterIncluded ? 'SB_Yes' : 'SB_No') | translate }}</dd>
            </div>
            <!-- The mode, not just the address: a Default on "No reply" has no address to show, and
                 showing nothing would read as "inherits" (SB-15933). -->
            <div>
              <dt>{{ 'SB_Reply_To' | translate }}</dt>
              <dd>{{ replyToModeLabel(d.replyToMode) | translate }}</dd>
            </div>
            <div *ngIf="d.replyToMode === replyToModes.Custom && d.replyToEmailAddress">
              <dt>{{ 'SB_Reply_To_Email' | translate }}</dt>
              <dd>{{ d.replyToEmailAddress }}</dd>
            </div>
          </dl>

          <!-- Tags for the selected user template (persisted immediately on add/remove). -->
          <div class="ctm__tags-edit" *ngIf="!viewingDefault && isUserTemplate">
            <label class="control-label">{{ 'SB_Tags' | translate }}</label>
            <div class="ctm__tags-list">
              <span class="ctm__tag ctm__tag--editable" *ngFor="let tag of userTags">
                {{ tag }}
                <button type="button" class="ctm__tag-remove" (click)="removeTag(tag)" [attr.aria-label]="'SB_Remove' | translate">×</button>
              </span>
              <input
                class="form-control form-control-sm ctm__tag-input"
                type="text"
                maxlength="100"
                [(ngModel)]="tagInput"
                [ngModelOptions]="{ standalone: true }"
                [placeholder]="'SB_Add_Tag' | translate"
                (keydown.enter)="$event.preventDefault(); addTag()"
                (blur)="addTag()" />
            </div>
          </div>
        </section>

        <!-- Versions of the selected template: choose one from the dropdown, save-as-new, activate, delete. -->
        <section class="ctm__card ctm__versions" *ngIf="!loadingTemplate && template">
          <div class="ctm__versions-row">
            <div class="ctm__versions-pick">
              <label class="ctm__eyebrow" [attr.for]="renamingVersionId == null ? 'ctmVersionSelect' : 'ctmVersionName'">
                {{ 'SB_Versions' | translate }}
              </label>

              <!-- Renaming a version (SB-17138): its name in place of the dropdown, saved on its own. -->
              <input
                *ngIf="renamingVersionId != null"
                id="ctmVersionName"
                type="text"
                class="form-control ctm__versions-select"
                maxlength="200"
                [(ngModel)]="versionNameDraft"
                [placeholder]="('SB_Version' | translate) + ' ' + (selectedVersion?.versionNumber ?? '')"
                [attr.aria-label]="'SB_Name' | translate"
                [disabled]="mutatingVersion"
                (keydown.enter)="saveVersionName()"
                (keydown.escape)="cancelRenameVersion()" />

              <select
                *ngIf="renamingVersionId == null"
                id="ctmVersionSelect"
                class="form-control ctm__versions-select"
                [ngModel]="selectedVersionId"
                (ngModelChange)="onVersionChange($event)"
                [disabled]="versionSelectDisabled">
                <option *ngIf="!versions.length" [ngValue]="null">{{ 'SB_Draft_Not_Saved' | translate }}</option>
                <optgroup *ngIf="versions.length" [label]="'SB_Versions' | translate">
                  <option *ngFor="let v of versions" [ngValue]="v.id">
                    {{ (v.name || (('SB_Version' | translate) + ' ' + v.versionNumber)) }}{{ v.isActive ? ' (' + ('SB_ACTIVE' | translate) + ')' : '' }}
                  </option>
                </optgroup>
                <optgroup *ngIf="canSelectDefault" [label]="'SB_Platform' | translate">
                  <option [ngValue]="defaultVersionId">{{ 'SB_Default_Version' | translate }}</option>
                </optgroup>
              </select>

              <button
                type="button"
                class="btn btn-default"
                *ngIf="renamingVersionId == null && selectedVersion && !viewingDefault"
                [disabled]="!canRenameVersion"
                [attr.aria-label]="'SB_Rename_Version' | translate"
                [ngbTooltip]="'SB_Rename_Version' | translate"
                (click)="startRenameVersion()">
                <sb-icon name="edit"></sb-icon>
              </button>

              <!-- Whether the version on screen is the one emails go out from. -->
              <span
                class="ctm__status"
                *ngIf="!viewingDefault && selectedVersion as v"
                [class.ctm__status--active]="v.isActive"
                [class.ctm__status--draft]="!v.isActive">
                {{ (v.isActive ? 'SB_Version_Status_Active' : 'SB_Version_Status_Draft') | translate }}
              </span>

              <!-- Who last changed the selected version, and when (SB-15966). Shared by Settings and SysAdmin. -->
              <span class="ctm__version-change" *ngIf="selectedVersionChange as change">
                {{ change.label | translate: { name: change.name, date: (change.date | date: 'dd MMM yyyy HH:mm') } }}
              </span>
            </div>

            <!--
              Every action on the template in one row, primary last: Preview, Delete version, Save as new
              version, Save and Activate on the far right. Save sits beside Save as new version: one writes
              the version on screen, the other a new one from it - which a custom template being created
              cannot have until its first save.
            -->
            <!-- While a version is renamed, its Save and Cancel are the only actions in the row. -->
            <div class="ctm__versions-actions" *ngIf="renamingVersionId != null">
              <button type="button" class="btn btn-default" (click)="cancelRenameVersion()" [disabled]="mutatingVersion">
                {{ 'SB_Cancel' | translate }}
              </button>
              <button type="button" class="btn btn-success" (click)="saveVersionName()" [disabled]="mutatingVersion">
                {{ 'SB_Save' | translate }}
              </button>
            </div>

            <div class="ctm__versions-actions" *ngIf="!viewingDefault && renamingVersionId == null">
              <button type="button" class="btn btn-secondary" (click)="preview()" [disabled]="!canPreview">
                {{ 'SB_Preview' | translate }}
              </button>

              <ng-container *ngIf="selectedVersion && !selectedVersion.isActive">
                <button
                  type="button"
                  class="btn btn-danger"
                  *ngIf="confirmingDeleteVersionId !== selectedVersion.id"
                  (click)="askDeleteVersion(selectedVersion)">
                  {{ 'SB_Delete_Version' | translate }}
                </button>
                <ng-container *ngIf="confirmingDeleteVersionId === selectedVersion.id">
                  <button type="button" class="btn btn-danger" (click)="deleteVersion(selectedVersion)">
                    {{ 'SB_Confirm' | translate }}
                  </button>
                  <button type="button" class="btn btn-default" (click)="cancelDeleteVersion()">
                    {{ 'SB_Cancel' | translate }}
                  </button>
                </ng-container>
              </ng-container>

              <button
                type="button"
                class="btn ctm__btn-secondary"
                *ngIf="!isNewUserTemplate"
                (click)="saveAsNewVersion()"
                [disabled]="!canSaveAsNewVersion">
                {{ 'SB_Save_As_New_Version' | translate }}
              </button>

              <button type="button" class="btn btn-success save-button" (click)="save()" [disabled]="!canSave">
                <sb-icon name="save"></sb-icon> {{ 'SB_Save' | translate }}
              </button>

              <button
                type="button"
                class="btn btn-primary"
                *ngIf="selectedVersion && !selectedVersion.isActive"
                (click)="activateVersion(selectedVersion)">
                {{ 'SB_Activate' | translate }}
              </button>
            </div>
          </div>

          <div *ngIf="loadingVersions" class="ctm__hint">{{ 'SB_Loading' | translate }}</div>
          <div *ngIf="!loadingVersions && !versions.length && !viewingDefault" class="ctm__hint">{{ 'SB_No_Versions' | translate }}</div>
          <div class="ctm__hint" *ngIf="isSeededFromDefault && !viewingDefault">{{ 'SB_Seeded_From_Default' | translate }}</div>
          <div class="ctm__hint" *ngIf="viewingDefault">
            <span class="ctm__badge--readonly">{{ 'SB_Read_Only' | translate }}</span>
            {{ 'SB_Default_Version_Hint' | translate }}
          </div>
        </section>

        <!--
          The editor stays mounted while the read-only Default is shown: the overlay covers it rather
          than replacing it, so the author's (possibly unsaved) work survives the peek, returning is
          instant, and Unlayer is never re-created (it reads its options once and never destroys its
          iframe, so remounting would leak one each time).
        -->
        <div class="ctm__canvas" [hidden]="!selectionKind">
          <!-- Unlayer only says that an image upload failed; this says why. -->
          <div class="alert alert-danger" *ngIf="imageError">
            {{ imageError | translate: imageErrorParams }}
          </div>

          <!--
            [hidden] on the canvas, not *ngIf on the editor: hasSelection stays true for the life of
            the component so Unlayer mounts once and is reused (it never destroys its iframe). But
            resetSelection() — which an organisation switch in the SysAdmin host triggers — clears the
            selection without unmounting, so without this the previous organisation's content stayed on
            screen underneath the "Select a template" hint.
          -->
          <sb-template-editor
            *ngIf="hasSelection"
            [design]="design"
            [mergeTags]="editorMergeTags"
            [projectId]="unlayerProjectId"
            [scriptUrl]="unlayerScriptUrl"
            [imageUploader]="imageUploader"
            [options]="editorBrandOptions"
            [minHeight]="'60rem'"
            (ready)="onEditorReady()"
            (canvasDirtyChange)="onCanvasDirtyChange($event)"
            (imageUploadFailed)="onImageUploadFailed($event)"
            (htmlExport)="onHtmlExport($event)">
          </sb-template-editor>

          <div
            class="ctm__default-view"
            *ngIf="viewingDefault"
            role="region"
            [attr.aria-label]="'SB_Default_Version' | translate">
            <div *ngIf="loadingDefault" class="ctm__hint">{{ 'SB_Loading' | translate }}</div>
            <div *ngIf="!loadingDefault && defaultIsEmpty" class="ctm__hint">{{ 'SB_Default_Empty' | translate }}</div>
            <div *ngIf="!loadingDefault && defaultRenderError" class="ctm__hint">{{ 'SB_Default_Load_Failed' | translate }}</div>

            <!--
              Push-only types (the transport notifications) have no email body by design, so there is
              nothing for the preview iframe to render. Show the push text itself rather than claiming
              the template has no content.
            -->
            <ng-container *ngIf="!loadingDefault && defaultIsPushOnly">
              <div class="ctm__hint">{{ 'SB_Push_Only_Default' | translate }}</div>
              <dl class="ctm__default-meta">
                <div>
                  <dt>{{ 'SB_Push_Notification_Body' | translate }}</dt>
                  <dd class="ctm__push-text">{{ defaultTemplate?.pushNotification }}</dd>
                </div>
              </dl>
            </ng-container>

            <ng-container *ngIf="!loadingDefault && !defaultIsEmpty && !defaultIsPushOnly && !defaultRenderError">

              <!-- Advisory: tags the renderer does not resolve reach the recipient as literal text (SB-14895). -->
              <div class="alert alert-warning ctm__unrecognised-tags" *ngIf="defaultUnrecognisedTags.length">
                {{ 'SB_Unrecognised_Merge_Tags' | translate: { tags: defaultUnrecognisedTags.join(', ') } }}
              </div>

              <div class="btn-group btn-group-sm ctm__preview-devices" role="group">
                <button
                  type="button"
                  class="btn"
                  [class.btn-primary]="defaultPreviewDevice === 'desktop'"
                  [class.btn-outline-primary]="defaultPreviewDevice !== 'desktop'"
                  (click)="defaultPreviewDevice = 'desktop'">
                  {{ 'SB_Preview_Desktop' | translate }}
                </button>
                <button
                  type="button"
                  class="btn"
                  [class.btn-primary]="defaultPreviewDevice === 'mobile'"
                  [class.btn-outline-primary]="defaultPreviewDevice !== 'mobile'"
                  (click)="defaultPreviewDevice = 'mobile'">
                  {{ 'SB_Preview_Mobile' | translate }}
                </button>
              </div>

              <div class="ctm__preview-stage" [class.ctm__preview-stage--mobile]="defaultPreviewDevice === 'mobile'">
                <iframe class="ctm__preview-frame" sandbox [srcdoc]="defaultSafeHtml"></iframe>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- Read-only action bar: no Save / Preview / Duplicate / Save-as-new while viewing the default. -->
        <div class="ctm__actions" *ngIf="!loadingTemplate && template && viewingDefault">
          <button type="button" class="btn btn-outline-secondary" (click)="exitDefault()">{{ 'SB_Back_To_Editor' | translate }}</button>

          <span class="ctm__actions-spacer"></span>

          <small class="ctm__hint" *ngIf="isSeededFromDefault">{{ 'SB_Editor_Already_Seeded_From_Default' | translate }}</small>

          <button
            type="button"
            class="btn btn-primary"
            *ngIf="canCopyFromDefault && !confirmingCopyDiscard"
            (click)="askCopyFromDefault()">
            {{ 'SB_Copy_Default_To_Version' | translate }}
          </button>

          <ng-container *ngIf="confirmingCopyDiscard">
            <small class="ctm__hint">{{ 'SB_Copy_Default_Discard_Warning' | translate }}</small>
            <button type="button" class="btn btn-primary" (click)="copyFromDefault()">{{ 'SB_Confirm' | translate }}</button>
            <button type="button" class="btn btn-secondary" (click)="cancelCopyFromDefault()">{{ 'SB_Cancel' | translate }}</button>
          </ng-container>
        </div>

        <!--
          What acts on a saved custom template as a whole, rather than on a version of it. Preview is in the
          version row, so there is nothing here for a system template or one not saved yet. Plain
          btn-default and btn-danger: nothing in this app styles the btn-outline-* variants.
        -->
        <div
          class="ctm__actions"
          *ngIf="!loadingTemplate && template && !viewingDefault && isUserTemplate && !isNewUserTemplate">
          <button type="button" class="btn btn-default" *ngIf="canDuplicate" (click)="duplicateCurrent()">
            {{ 'SB_Duplicate' | translate }}
          </button>

          <button type="button" class="btn btn-danger" *ngIf="!confirmingDeleteTemplate" (click)="askDeleteTemplate()">
            {{ 'SB_Delete' | translate }}
          </button>
          <ng-container *ngIf="confirmingDeleteTemplate">
            <button type="button" class="btn btn-danger" (click)="deleteTemplate()">
              {{ 'SB_Confirm' | translate }}
            </button>
            <button type="button" class="btn btn-default" (click)="cancelDeleteTemplate()">
              {{ 'SB_Cancel' | translate }}
            </button>
          </ng-container>
        </div>
      </div>
    </section>

    <!--
      The organisation's own email footer (SB-14896). A sibling of the editor pane, not a child of the
      18rem list pane, which is `flex: 0 0 18rem; overflow: hidden` and would clip the editor.

      The section stays mounted (its id is what the tablist's aria-controls names, and a spec asserts
      every tab points at a panel that exists) and is toggled with [hidden]; only the <editor> inside
      is lazily created, on first visit to the tab.
    -->
    <section
      class="ctm__pane ctm__pane--footer"
      id="ctmListPanel-footer"
      role="tabpanel"
      aria-labelledby="ctmListTab-footer"
      [hidden]="activeListTab !== 'footer'">
      <!-- The same card as the template editor's, with the primary action last on the right. -->
      <div class="ctm__footer ctm__card">
        <div>
          <h4 class="ctm__title">{{ 'SB_Email_Footer' | translate }}</h4>
          <p class="ctm__footer-description">{{ 'SB_Email_Footer_Description' | translate }}</p>
        </div>

        <div *ngIf="footerLoading" class="ctm__hint">{{ 'SB_Loading' | translate }}</div>

        <div class="ctm__footer-editor" *ngIf="!footerLoading">
          <editor
            *ngIf="footerTabVisited"
            [init]="footerTinymceOptions"
            [(ngModel)]="footerHtml"
            [ngModelOptions]="{ standalone: true }"
            (onInit)="onFooterEditorReady($event.editor)"
            (ngModelChange)="onFooterChanged()"></editor>

          <!-- SB-13366: put the SchoolsBuddy default footer back, or a part of it. Like typing, they only
               change the editor; nothing is stored until Save. -->
          <div class="ctm__footer-inserts">
            <div class="ctm__footer-inserts-buttons">
              <button
                type="button"
                id="ctmFooterInsertLink"
                class="btn btn-default"
                (click)="insertFooterSiteLink()"
                [disabled]="!canInsertIntoFooter"
              >
                {{ 'SB_Email_Footer_Insert_Site_Link' | translate }}
              </button>
              <button
                type="button"
                id="ctmFooterInsertLogo"
                class="btn btn-default"
                (click)="insertFooterLogo()"
                [disabled]="!canInsertFooterLogo"
                [attr.aria-describedby]="showFooterNoLogoHint ? 'ctmFooterNoLogoHint' : null"
              >
                {{ 'SB_Email_Footer_Insert_Logo' | translate }}
              </button>
              <button
                type="button"
                id="ctmFooterSetDefault"
                class="btn btn-default"
                (click)="setFooterToDefault()"
                [disabled]="!canSetFooterToDefault"
              >
                {{ 'SB_Set_To_Default' | translate }}
              </button>
            </div>
            <p class="ctm__hint ctm__footer-inserts-hint" id="ctmFooterNoLogoHint" *ngIf="showFooterNoLogoHint">
              {{ 'SB_Email_Footer_No_Logo' | translate }}
            </p>
          </div>

          <!-- Shown when saving stored something other than what was submitted (comments/<style> are
               dropped by the sanitiser), so the author is not left looking at markup that is not stored. -->
          <div class="alert alert-warning ctm__alert" *ngIf="footerRewritten">
            <p>{{ 'SB_Email_Footer_Sanitised_Warning' | translate }}</p>
          </div>

          <div class="ctm__footer-actions">
            <button type="button" class="btn ctm__btn-secondary" (click)="undoFooterChanges()" [disabled]="!footerDirty">
              {{ 'SB_Undo_Changes' | translate }}
            </button>
            <button type="button" class="btn btn-success save-button" (click)="saveFooter()" [disabled]="!canSaveFooter">
              <sb-icon name="save"></sb-icon> {{ 'SB_Save' | translate }}
            </button>
          </div>
        </div>

        <!--
          Read-only: what every email carries BELOW the footer above. Without this an admin will type
          the school's address and an unsubscribe line into the box and recipients get both twice, in
          two different styles, with no preview anywhere in the product that would reveal it.
          Rendered from the server so it cannot drift from what is actually sent.
        -->
        <div class="ctm__footer-compliance" *ngIf="footerCompliance as compliance">
          <h5 class="ctm__eyebrow">{{ 'SB_Email_Footer_Compliance_Title' | translate }}</h5>

          <ng-container *ngIf="compliance.isActive; else complianceInactive">
            <!-- SB-17145: the address in the block below comes from Main Settings, so say where to change it. -->
            <p class="ctm__footer-description">
              {{ 'SB_Email_Footer_Compliance_Description' | translate }}
              <a
                id="ctmFooterUpdateAddress"
                *ngIf="organisationSettingsRoute; else updateAddressText"
                [routerLink]="organisationSettingsRoute"
                >{{ 'SB_Email_Footer_Update_Address' | translate }}</a
              >
              <ng-template #updateAddressText>
                <span id="ctmFooterUpdateAddress">{{ 'SB_Email_Footer_Update_Address' | translate }}</span>
              </ng-template>
            </p>

            <div class="ctm__footer-compliance-preview">
              <div *ngIf="compliance.hasPostalAddress" [innerHTML]="complianceAddressSafeHtml"></div>
              <div class="alert alert-warning ctm__alert" *ngIf="!compliance.hasPostalAddress">
                <p>{{ 'SB_Email_Footer_No_Postal_Address' | translate }}</p>
              </div>
              <div [innerHTML]="complianceUnsubscribeSafeHtml"></div>
            </div>
          </ng-container>

          <!-- The block is produced only by the v2 send lane; where that is off, emails carry the
               footer and nothing else. Saying otherwise would be a false compliance claim. -->
          <ng-template #complianceInactive>
            <p class="ctm__footer-description">{{ 'SB_Email_Footer_Compliance_Inactive' | translate }}</p>
          </ng-template>
        </div>
      </div>
    </section>

    <!--
      Reply-To: the organisation's default (SB-15933). Like the footer pane this is a singleton editor
      and replaces the right-hand editor wholesale; it stays mounted so its id backs the tablist's
      aria-controls, and is toggled with [hidden].
    -->
    <section
      class="ctm__pane ctm__pane--footer"
      id="ctmListPanel-replyTo"
      role="tabpanel"
      aria-labelledby="ctmListTab-replyTo"
      [hidden]="activeListTab !== 'replyTo'">
      <div class="ctm__footer ctm__card ctm__reply-to-panel">
        <div>
          <h4 class="ctm__title">{{ 'SB_Reply_To' | translate }}</h4>
          <p class="ctm__footer-description">{{ 'SB_Reply_To_Description' | translate }}</p>
        </div>

        <div *ngIf="replyToLoading" class="ctm__hint">{{ 'SB_Loading' | translate }}</div>

        <ng-container *ngIf="!replyToLoading">
          <!--
            Reply-To is applied by the v2 send lane only. Where that is off the setting saves and does
            nothing, and letting a school find that out from a real email would be worse than saying so
            here. Reuses the footer-compliance flag, which reports the same lane.
          -->
          <div class="alert alert-warning ctm__alert" *ngIf="footerCompliance && !footerCompliance.isActive">
            <p>{{ 'SB_Reply_To_Not_Applied_Warning' | translate }}</p>
          </div>

          <!--
            Same shape as the per-template override below: a checkbox that reveals the modes, rather than
            a flat list where "not set" competes with the two real choices. Off is the default state and
            says nothing about an address - what it resolves to is the platform's business and can change,
            so the line under it states today's outcome instead of a mode label promising an address.
          -->
          <div class="ctm__reply-to-set">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="ctmSetReplyTo"
                [checked]="replyTo.replyToMode !== replyToModes.Inherit"
                (change)="onSetReplyToChange($event)" />
              <label class="form-check-label" for="ctmSetReplyTo">{{ 'SB_Set_Reply_To' | translate }}</label>
            </div>

            <div class="ctm__reply-to-modes" *ngIf="replyTo.replyToMode !== replyToModes.Inherit">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  id="ctmReplyToModeCustom"
                  name="ctmReplyToMode"
                  [checked]="replyTo.replyToMode === replyToModes.Custom"
                  (change)="onReplyToModeChange(replyToModes.Custom)" />
                <label class="form-check-label" for="ctmReplyToModeCustom">{{ 'SB_Reply_To_Mode_Custom' | translate }}</label>
              </div>

              <!-- The address pair belongs to Custom only; every other mode clears it server-side. -->
              <div class="ctm__reply-to" *ngIf="replyTo.replyToMode === replyToModes.Custom">
                <div class="form-group">
                  <label class="control-label" for="ctmReplyToEmail">{{ 'SB_Reply_To_Email' | translate }}</label>
                  <input
                    class="form-control"
                    type="email"
                    id="ctmReplyToEmail"
                    maxlength="320"
                    autocomplete="off"
                    [(ngModel)]="replyTo.defaultReplyToEmailAddress"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="onReplyToChanged()" />
                  <small class="text-danger" *ngIf="replyToAddressMissing">{{ 'SB_Reply_To_Required' | translate }}</small>
                </div>

                <div class="form-group">
                  <label class="control-label" for="ctmReplyToName">{{ 'SB_Reply_To_Name' | translate }}</label>
                  <input
                    class="form-control"
                    type="text"
                    id="ctmReplyToName"
                    maxlength="200"
                    [(ngModel)]="replyTo.defaultReplyToDisplayName"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="onReplyToChanged()" />
                </div>
              </div>

              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  id="ctmReplyToModeNoReply"
                  name="ctmReplyToMode"
                  [checked]="replyTo.replyToMode === replyToModes.NoReply"
                  (change)="onReplyToModeChange(replyToModes.NoReply)" />
                <label class="form-check-label" for="ctmReplyToModeNoReply">{{ 'SB_Reply_To_Mode_No_Reply' | translate }}</label>
              </div>
            </div>

            <!-- What the current state actually does to a sent email — the question the ticket asks. -->
            <p class="ctm__muted ctm__reply-to-guidance">{{ replyToGuidanceLabel | translate }}</p>
          </div>

          <div class="ctm__footer-actions">
            <button type="button" class="btn ctm__btn-secondary" (click)="undoReplyToChanges()" [disabled]="!replyToDirty">
              {{ 'SB_Undo_Changes' | translate }}
            </button>
            <button type="button" class="btn btn-success save-button" (click)="saveReplyTo()" [disabled]="!canSaveReplyTo">
              <sb-icon name="save"></sb-icon> {{ 'SB_Save' | translate }}
            </button>
          </div>
        </ng-container>
      </div>
    </section>

  </div>
</div>

<!-- Preview opens in a modal (sandboxed iframe of the server-rendered HTML). -->
<ng-template #previewModal let-modal>
  <sb-modal-header
    [modalTitle]="'SB_Preview'"
    description="SB_Template_Preview_Note"
    (onClose)="modal.dismiss()"></sb-modal-header>
  <div class="modal-body ctm__preview-body">
    <!-- Advisory: tags the renderer does not resolve reach the recipient as literal text (SB-14895). -->
    <div class="alert alert-warning ctm__unrecognised-tags" *ngIf="previewUnrecognisedTags.length">
      {{ 'SB_Unrecognised_Merge_Tags' | translate: { tags: previewUnrecognisedTags.join(', ') } }}
    </div>

    <!-- Desktop / mobile device toggle (SB-14894 A): constrains the preview iframe width. -->
    <div class="ctm__preview-devices btn-group" role="group">
      <button type="button" class="btn btn-sm"
        [class.btn-primary]="previewDevice === 'desktop'" [class.btn-outline-secondary]="previewDevice !== 'desktop'"
        (click)="previewDevice = 'desktop'">
        {{ 'SB_Preview_Desktop' | translate }}
      </button>
      <button type="button" class="btn btn-sm"
        [class.btn-primary]="previewDevice === 'mobile'" [class.btn-outline-secondary]="previewDevice !== 'mobile'"
        (click)="previewDevice = 'mobile'">
        {{ 'SB_Preview_Mobile' | translate }}
      </button>
    </div>

    <div class="ctm__preview-stage" [class.ctm__preview-stage--mobile]="previewDevice === 'mobile'">
      <iframe class="ctm__preview-frame" sandbox [srcdoc]="previewSafeHtml"></iframe>
    </div>
  </div>
  <div class="modal-footer ctm__preview-footer">
    <div class="ctm__preview-footer-main">
      <!--
        Only while the v2 lane is on: that lane is what inserts the unsubscribe block, and claiming it
        otherwise would be a false compliance statement (see the footer pane).
      -->
      <p class="ctm__preview-note" *ngIf="footerCompliance?.isActive">{{ 'SB_Template_Unsubscribe_Note' | translate }}</p>

      <!-- Test send (SB-14894 B): sends the SAVED template with sample data to the entered address. -->
      <div class="ctm__test-send" *ngIf="testSendEnabled">
        <label class="ctm__test-send-label" for="ctmTestEmail">{{ 'SB_Test_Email_Address' | translate }}</label>
        <input id="ctmTestEmail" type="email" class="form-control form-control-sm ctm__test-send-input"
          [(ngModel)]="testEmail" [placeholder]="'SB_Test_Email_Address' | translate" autocomplete="off" />
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          (click)="sendTest()"
          [disabled]="!canSendTest"
          [attr.aria-describedby]="testSendNeedsSave ? 'ctmTestSendHint' : null"
        >
          {{ 'SB_Send_Test' | translate }}
        </button>
        <!-- Same getter as the button's gate (SB-17217): whenever unsaved work turns Send test off, say so. -->
        <span id="ctmTestSendHint" class="ctm__test-send-hint text-muted" *ngIf="testSendNeedsSave">
          {{ 'SB_Save_Before_Test' | translate }}
        </span>
      </div>
    </div>
    <button type="button" class="btn btn-secondary" (click)="modal.close()">{{ 'SB_Close' | translate }}</button>
  </div>
</ng-template>

<!-- "New template": name + Blank | Pre-built (the pre-built gallery = platform system-org templates). -->
<ng-template #createModal let-modal>
  <sb-modal-header [modalTitle]="'SB_New_Template'" (onClose)="modal.dismiss()"></sb-modal-header>
  <div class="modal-body ctm__create-body">
    <div class="form-group">
      <label class="control-label">{{ 'SB_Template_Name' | translate }}</label>
      <input class="form-control" type="text" maxlength="200" [(ngModel)]="createName" [ngModelOptions]="{ standalone: true }" />
    </div>

    <div class="ctm__create-modes">
      <label class="form-check-label">
        <input class="form-check-input" type="radio" name="createMode" [checked]="createMode === 'blank'" (change)="onCreateModeChange('blank')" />
        {{ 'SB_Blank_Template' | translate }}
      </label>
      <label class="form-check-label">
        <input class="form-check-input" type="radio" name="createMode" [checked]="createMode === 'prebuilt'" (change)="onCreateModeChange('prebuilt')" />
        {{ 'SB_Prebuilt_Template' | translate }}
      </label>
    </div>

    <div class="ctm__prebuilt" *ngIf="createMode === 'prebuilt'">
      <div *ngIf="loadingPrebuilt" class="ctm__hint">{{ 'SB_Loading' | translate }}</div>
      <div *ngIf="!loadingPrebuilt && !prebuiltTemplates.length" class="ctm__hint">{{ 'SB_No_Prebuilt_Templates' | translate }}</div>

      <ng-container *ngIf="!loadingPrebuilt && prebuiltTemplates.length">
        <input
          class="form-control ctm__prebuilt-filter"
          type="text"
          [(ngModel)]="prebuiltFilter"
          (ngModelChange)="onPrebuiltFilterChange()"
          [ngModelOptions]="{ standalone: true }"
          [placeholder]="'SB_Search' | translate" />

        <div *ngIf="!prebuiltByCategory.length" class="ctm__hint">{{ 'SB_Library_No_Entries' | translate }}</div>

        <!-- Grouped by category, not by template type: there is exactly one entry per type. -->
        <div class="ctm__prebuilt-group" *ngFor="let group of prebuiltByCategory">
          <h6 class="ctm__prebuilt-category">{{ group.category }}</h6>
          <ul class="ctm__list">
            <li
              class="ctm__item"
              *ngFor="let pb of group.entries; trackBy: trackPrebuilt"
              [class.ctm__item--active]="selectedPrebuiltId === pb.id"
              (click)="selectedPrebuiltId = pb.id">
              <span class="ctm__item-name">{{ pb.name }}</span>
              <span class="ctm__item-meta" *ngIf="pb.description">{{ pb.description }}</span>
              <span class="ctm__item-badge" *ngIf="pb.hasPushText">{{ 'SB_Push_Notification' | translate }}</span>
            </li>
          </ul>
        </div>
      </ng-container>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">{{ 'SB_Cancel' | translate }}</button>
    <button type="button" class="btn btn-primary" (click)="confirmCreate(modal)" [disabled]="!canCreate">{{ 'SB_Create' | translate }}</button>
  </div>
</ng-template>
